import { ComponentType, RefObject, SVGAttributes, useEffect, useRef, useState } from 'react';
import { useOnClickOutside } from 'usehooks-ts';

import AcceptConflict from '@/assets/icons/AcceptConflict';
import EyeFalse from '@/assets/icons/EyeFalse';
import Werklijndiagram from '@/assets/icons/Werklijndiagram';
import { useHasPermission } from '@/auth';
import {
  ContextMenu,
  SidePanelContextMenuInformation,
  WerklijnDialogType,
} from '@/components/contextMenu/mwdContextMenu/types';
import useClientState from '@/state/useClientState';

import styles from '../ContextMenu.module.css';

const contextMenuIconMap: Record<string, ComponentType<SVGAttributes<SVGSVGElement>>> = {
  Werklijndiagram,
  AcceptConflict,
  EyeFalse,
};

export const SidebarContextMenu = ({
  data,
  blockOtherElements,
  handleGeselecteerdUitPanel,
  labels,
  iconNames,
  sidebarWidth,
  sidebarHeight,
  setActivePanel,
  ...boxProps
}: ContextMenu) => {
  const [opacity, setOpacity] = useState<number>(0);
  const [marginLeft, setMarginLeft] = useState<number>(-1000);
  const [marginTop, setMarginTop] = useState<number>(-1000);
  const setShowDialog = useClientState((state) => state.setShowDialog);
  const ref = useRef<HTMLDivElement>(null);
  const isAllowedToInregelingen = useHasPermission('mwd.inregelingen');

  const handleCloseMenu = () => {
    setOpacity(0);
    setMarginLeft(-1000);
    setMarginTop(-1000);
    blockOtherElements?.(false);
  };

  const getShortcutForKey = (key: string) => {
    switch (key) {
      case 'toevoegen-mwd':
        return 'Alt + T';
      case 'signaal-verwijderen':
        return 'Delete';
      default:
        return null;
    }
  };

  const handleClick = (label: WerklijnDialogType, isAllowedToInregelingen?: boolean) => {
    if (label === 'toevoegen-mwd') {
      if (handleGeselecteerdUitPanel) {
        handleGeselecteerdUitPanel((data as SidePanelContextMenuInformation).criteria);
      }
      setActivePanel?.(undefined);
    } else if (label === 'accepteer-conflict' && isAllowedToInregelingen) {
      if (handleGeselecteerdUitPanel) {
        handleGeselecteerdUitPanel((data as SidePanelContextMenuInformation).criteria);

        if (data && 'options' in data) {
          setShowDialog('accepteer-conflict', 'sidepanel', data.options);
        }
      }
      setActivePanel?.(undefined);
    } else if (label === 'signaal-verwijderen') {
      updateDeletedSignalen();
      setActivePanel?.(undefined);
    }

    handleCloseMenu();
  };

  useOnClickOutside(ref as RefObject<HTMLElement>, handleCloseMenu);

  const updateDeletedSignalen = () => {
    const parsedSelectedSignalen = getDataUitSessieEnParse('selectedSignalen');
    const parsedDeletedSignalen = getDataUitSessieEnParse('deletedSignalen');

    const updatedDeletedSignalen = Array.from(
      new Set([...parsedDeletedSignalen, ...parsedSelectedSignalen.map((signaal) => signaal.berichtId)]),
    );

    sessionStorage.setItem('deletedSignalen', JSON.stringify(updatedDeletedSignalen));
    sessionStorage.setItem('selectedSignalen', JSON.stringify([]));
    window.dispatchEvent(new Event('storage'));
  };

  const getDataUitSessieEnParse = (key: string) => {
    const item = sessionStorage.getItem(key);
    return item ? JSON.parse(item) : [];
  };

  const isClickAtBottom = (pageY: number, threshold = 120) => {
    return pageY >= window.scrollY + window.innerHeight - threshold;
  };

  const onActiveState = () => {
    if (data?.active) {
      setOpacity(1);
      let marginTop = 100;
      if (data && sidebarHeight) {
        setMarginLeft((sidebarWidth || 0) / 2 - 150);
        const calculatedHeight = isClickAtBottom(data.pageY) ? data.pageY - 180 : data.pageY - 65;
        marginTop = Math.round(calculatedHeight);
      }
      setMarginTop(marginTop);
      blockOtherElements?.(true);
    } else {
      setOpacity(0);
      setMarginLeft(-1000);
      setMarginTop(-1000);
      blockOtherElements?.(false);
    }
  };

  useEffect(() => {
    onActiveState();
  }, [data]);

  return (
    <div
      style={{
        opacity,
        left: `${marginLeft}px`,
        top: `${marginTop}px`,
      }}
      ref={ref}
      className={styles.contextMenu}
      data-testid={`contextMenu-${data?.labels}`}
      {...boxProps}
    >
      <ul className={styles.contextInnerMenu}>
        {labels?.map((label, index) => {
          const shortcut = getShortcutForKey(label.key);
          const IconComponent = iconNames ? contextMenuIconMap[iconNames[index]] : null;

          return (
            <li
              style={{ color: 'var(--tractie-color-content-emphasis-default)' }}
              tabIndex={0}
              key={label.key}
              data-testid={label.key}
              onClick={() => handleClick(label.key, isAllowedToInregelingen)}
              onKeyDown={(e) => e.key === 'Enter' && handleClick(label.key, isAllowedToInregelingen)}
              className={styles.contextInnerMenuListItem}
            >
              <div className={styles.contentWrapper}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  {IconComponent && (
                    <IconComponent
                      aria-hidden='true'
                      width='22px'
                      height='22px'
                      fill='var(--tractie-color-content-emphasis-default)'
                    />
                  )}
                  {label.value}
                </div>
                {shortcut && <span className={styles.shortcut}>{shortcut}</span>}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
};
